<template>
  <Page>
    <div class="flex h-full flex-col gap-3">
      <!-- Header -->
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden p-6">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
              <i class="fa-solid fa-shield-halved text-xl"></i>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-primary">Integrity Check</h1>
              <p class="text-secondary">Scan asset data for referential integrity violations</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="pi pi-file-export"
              label="Export CSV"
              severity="secondary"
              outlined
              class="rounded-full"
              :disabled="isLoading || filteredViolations.length === 0"
              @click="handleExportCsv"
            />
            <Button
              icon="pi pi-play"
              label="Run Check"
              class="rounded-full"
              :loading="isLoading"
              @click="handleRunCheck"
            />
          </div>
        </div>
      </div>

      <!-- Summary cards -->
      <div v-if="report" class="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Assets Checked</p>
          <p class="mt-1 text-2xl font-bold text-text">{{ report.totalAssetsChecked }}</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Total Violations</p>
          <p class="mt-1 text-2xl font-bold text-text">{{ report.totalViolationsFound }}</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-danger/40 p-4 text-center">
          <p class="text-xs font-semibold text-danger uppercase tracking-wide">Errors</p>
          <p class="mt-1 text-2xl font-bold text-danger">{{ report.errorCount }}</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-warning/40 p-4 text-center">
          <p class="text-xs font-semibold text-warning uppercase tracking-wide">Warnings</p>
          <p class="mt-1 text-2xl font-bold text-warning">{{ report.warningCount }}</p>
        </div>
        <div class="bg-background-card rounded-lg shadow-card border border-border p-4 text-center">
          <p class="text-xs font-semibold text-text-light uppercase tracking-wide">Duration</p>
          <p class="mt-1 text-2xl font-bold text-text">{{ formatDuration(report.durationMs) }}</p>
        </div>
      </div>

      <!-- Filters -->
      <div v-if="report" class="bg-background-card rounded-lg shadow-card overflow-hidden p-4">
        <div class="rounded-lg border border-border bg-background-elevated p-4">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-text">Filters</h2>
            <div class="flex items-center gap-2">
              <span class="text-xs text-text-light">
                {{ filteredViolations.length }} of {{ violations.length }}
                {{ violations.length === 1 ? 'violation' : 'violations' }}
              </span>
              <Button
                icon="pi pi-filter-slash"
                label="Clear"
                severity="secondary"
                outlined
                size="small"
                class="rounded-full"
                :disabled="!hasActiveFilters"
                @click="resetFilters"
              />
            </div>
          </div>
          <div class="mt-3 grid grid-cols-12 gap-3">
            <div class="col-span-12 lg:col-span-4">
              <label class="text-xs font-semibold text-text-light">Search</label>
              <InputText
                v-model="filters.searchText"
                class="w-full"
                placeholder="Search asset ID, field, entity, or message"
              />
            </div>
            <div class="col-span-12 md:col-span-4 lg:col-span-3">
              <label class="text-xs font-semibold text-text-light">Severity</label>
              <Dropdown
                v-model="filters.severity"
                :options="severityOptions"
                option-label="label"
                option-value="value"
                class="w-full"
              />
            </div>
            <div class="col-span-12 md:col-span-4 lg:col-span-3">
              <label class="text-xs font-semibold text-text-light">Rule</label>
              <Dropdown
                v-model="filters.ruleName"
                :options="ruleNameOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                show-clear
                placeholder="All rules"
              />
            </div>
            <div class="col-span-12 md:col-span-4 lg:col-span-2">
              <label class="text-xs font-semibold text-text-light">Entity Type</label>
              <Dropdown
                v-model="filters.entityType"
                :options="entityTypeOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                show-clear
                placeholder="All types"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Results table -->
      <div
        class="relative flex min-h-[20rem] flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card"
      >
        <!-- Initial state -->
        <div
          v-if="!report && !isLoading && !error"
          class="flex flex-1 items-center justify-center px-6 py-10"
        >
          <div class="max-w-md text-center">
            <i class="fa-solid fa-shield-halved text-4xl text-text-light mb-3"></i>
            <h2 class="text-base font-semibold text-text">No Check Run Yet</h2>
            <p class="mt-1 text-sm text-text-light">
              Click "Run Check" to scan asset data for integrity violations.
            </p>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light"
        >
          <i class="pi pi-spin pi-spinner mr-2"></i>
          Running integrity check... This may take a moment.
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-danger"
        >
          {{ error }}
        </div>

        <!-- Empty results -->
        <div
          v-else-if="report && filteredViolations.length === 0"
          class="flex flex-1 items-center justify-center px-6 py-10"
        >
          <div class="max-w-md text-center">
            <i class="fa-solid fa-circle-check text-4xl text-success mb-3"></i>
            <h2 class="text-base font-semibold text-text">
              {{ hasActiveFilters ? 'No violations match the current filters' : 'No Violations Found' }}
            </h2>
            <p class="mt-1 text-sm text-text-light">
              {{
                hasActiveFilters
                  ? 'Try adjusting your filters.'
                  : 'All asset data passed the integrity checks.'
              }}
            </p>
          </div>
        </div>

        <!-- Data table -->
        <div v-else-if="report && filteredViolations.length > 0" class="flex-1 overflow-hidden">
          <DataTable
            :value="filteredViolations"
            data-key="index"
            scrollable
            scroll-height="flex"
            striped-rows
            show-gridlines
            class="p-datatable-sm"
            :paginator="filteredViolations.length > 50"
            :rows="50"
            :rows-per-page-options="[25, 50, 100]"
          >
            <Column field="assetId" header="Asset ID" style="min-width: 10rem" sortable>
              <template #body="{ data }">
                <span class="font-semibold">{{ data.assetId || '—' }}</span>
              </template>
            </Column>

            <Column field="entityType" header="Entity Type" style="min-width: 10rem" sortable>
              <template #body="{ data }">
                {{ data.entityType || '—' }}
              </template>
            </Column>

            <Column field="fieldName" header="Field" style="min-width: 10rem" sortable>
              <template #body="{ data }">
                <code class="text-xs bg-background-elevated px-1.5 py-0.5 rounded">{{
                  data.fieldName || '—'
                }}</code>
              </template>
            </Column>

            <Column header="Referenced Entity" style="min-width: 14rem">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <span class="text-xs text-text-light">{{ data.referencedEntityType || '—' }}</span>
                  <span>{{ data.referencedEntityDisplayName || data.referencedEntityId || '—' }}</span>
                </div>
              </template>
            </Column>

            <Column field="ruleName" header="Rule" style="min-width: 10rem" sortable>
              <template #body="{ data }">
                {{ data.ruleName || '—' }}
              </template>
            </Column>

            <Column field="severity" header="Severity" style="min-width: 7rem" sortable>
              <template #body="{ data }">
                <Tag
                  :value="data.severity"
                  :severity="data.severity === 'Error' ? 'danger' : 'warn'"
                />
              </template>
            </Column>

            <Column field="message" header="Message" style="min-width: 20rem">
              <template #body="{ data }">
                <span class="text-sm">{{ data.message || '—' }}</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </Page>
</template>

<script setup lang="ts">
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';
import { useIntegrityCheck } from '../composables/useIntegrityCheck';

definePage({
  meta: {
    title: 'Integrity Check',
  },
});

const toast = useToast();

const {
  report,
  isLoading,
  error,
  filters,
  violations,
  filteredViolations,
  ruleNameOptions,
  entityTypeOptions,
  hasActiveFilters,
  runCheck,
  resetFilters,
  exportToCsv,
} = useIntegrityCheck();

const severityOptions = [
  { label: 'All Severities', value: 'all' },
  { label: 'Error', value: 'Error' },
  { label: 'Warning', value: 'Warning' },
];

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

async function handleRunCheck(): Promise<void> {
  await runCheck();

  if (error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Check Failed',
      detail: error.value,
    });
  } else if (report.value) {
    const { totalViolationsFound, errorCount, warningCount } = report.value;
    if (totalViolationsFound === 0) {
      addToast(toast, {
        severity: 'success',
        summary: 'All Clear',
        detail: 'No integrity violations found.',
      });
    } else {
      addToast(toast, {
        severity: errorCount && errorCount > 0 ? 'error' : 'warn',
        summary: 'Violations Found',
        detail: `${totalViolationsFound} violation(s): ${errorCount} error(s), ${warningCount} warning(s).`,
      });
    }
  }
}

function handleExportCsv(): void {
  exportToCsv();
  addToast(toast, {
    severity: 'success',
    summary: 'Export Complete',
    detail: `${filteredViolations.value.length} violation(s) exported to CSV.`,
  });
}
</script>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
  @apply bg-background-elevated text-xs font-semibold uppercase tracking-wide text-text-light;
}
</style>
